import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { resolveOAuthClientId } from '@app/auth-client.resolver';
import { loadAuthConfig } from '@app/auth.config';
import { Store } from '@ngrx/store';
import { OFFICE_HOST_TYPE } from '@shared/tokens';
import * as AccountActions from '@store/actions/account.actions';
import * as FavoritesActions from '@store/actions/favorites.actions';
import * as ItemsActions from '@store/actions/items.actions';
import { OAuthErrorEvent, OAuthInfoEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, Subject, combineLatest, fromEvent, takeUntil } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { AccountService } from './account.service';
import { AddinOauthService } from './addin-oauth-service.service';
import { LoggerService } from './logger.service';
import { SettingsService } from './settings.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private oauthService = inject(AddinOauthService);
  private officeHost = inject<Office.HostType | null>(OFFICE_HOST_TYPE);
  private settingsService = inject(SettingsService);
  private http = inject(HttpClient);
  private store = inject(Store);
  private accountService = inject(AccountService);
  private loggerService = inject(LoggerService);

  private hasLoadedAccountDetails = false;

  protected $ngDestroyed = new Subject<void>();

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  private isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isValidBaseUrlSubject$ = new BehaviorSubject<boolean>(false);
  private isValidBaseUrl$ = this.isValidBaseUrlSubject$.asObservable();

  public canCallApi$: Observable<boolean> = combineLatest([this.isAuthenticated$, this.isValidBaseUrl$]).pipe(
    map((values) => values.every((b) => b))
  );

  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  initialize() {
    const baseUrl = this.settingsService.getParsedBaseUrl();
    const clientId = resolveOAuthClientId(this.officeHost);

    this.loggerService.debug('[AuthService] Resolved OAuth host and client ID:', this.officeHost ?? 'widget/non-Office', clientId);

    if (baseUrl) {
      this.oauthService.configure(loadAuthConfig(baseUrl, clientId));
      this.isValidBaseUrlSubject$.next(true);
    }
    // setup listener for changes to the baseUrl
    this.settingsService.settingsChange$.pipe(takeUntil(this.$ngDestroyed)).subscribe((change) => {
      const prev = change.previous == null ? '' : change.previous.baseUrl;
      const cur = change.current == null ? '' : change.current.baseUrl;
      const valid = this.settingsService.hasValidBaseUrl();
      this.isValidBaseUrlSubject$.next(valid);
      if (cur !== prev && valid) {
        this.oauthService.configure(loadAuthConfig(this.settingsService.getParsedBaseUrl(), clientId));
        // if user was already logged in, but base url changed, log them out
        if (this.oauthService.hasValidAccessToken()) {
          this.logout();
        }
      }
    });

    this.oauthService.events.pipe(takeUntil(this.$ngDestroyed)).subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        this.loggerService.error('[AuthService] OAuthErrorEvent:', event);
      } else if (event instanceof OAuthInfoEvent) {
        this.loggerService.debug('[AuthService] OAuthInfoEvent Object:', event);
        if (event.type === 'token_expires') {
          void this.oauthService
            .refreshToken()
            .then(() => {
              this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
            })
            .catch((error) => {
              this.loggerService.error('[AuthService] Refresh token failed:', error);
              this.logout();
            });
        }
      } else {
        this.loggerService.debug('[AuthService] OAuthEvent Object:', event);
      }
      if (event.type !== 'token_expires') {
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
      }
    });

    // TODO: add support for notifying user about pupup blockers ^^^
    // OAuthErrorEvent: {"type":"popup_blocked","reason":{},"params":null}
    // TODO: add support for expiring of tokens (this should have happened above but did not)
    // OAuthEvent Object: OAuthInfoEvent({type: 'token_expires'});
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntil(this.$ngDestroyed))
      .subscribe((event: StorageEvent) => {
        if (event.key !== 'access_token' && event.key !== null) return;
        this.loggerService.warn('[AuthService] Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
      });

    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.canCallApi$.pipe(takeUntil(this.$ngDestroyed)).subscribe((canCallApi) => {
      if (canCallApi && !this.hasLoadedAccountDetails) {
        this.store.dispatch(AccountActions.loadAccountDetails());
        this.hasLoadedAccountDetails = true;
      } else {
        this.hasLoadedAccountDetails = false;
      }
    });
  }

  public login() {
    this.oauthService.initLoginFlowInPopup();
  }

  public logout(clearEfrSession = false) {
    this.accountService.resetAccountDetailsLoaded();
    // ensure state is reset
    this.store.dispatch(ItemsActions.resetItems());
    this.store.dispatch(AccountActions.resetAccountState());
    this.store.dispatch(FavoritesActions.resetFavoritesState());
    // perform logout
    if (clearEfrSession) {
      if (this.settingsService.hasValidBaseUrl()) {
        this.http
          .get(`${this.settingsService.getParsedBaseUrl()}logout.json`, {
            observe: 'response',
            withCredentials: true,
          })
          .pipe(finalize(() => this.oauthService.logOut()))
          .subscribe();
      }
    } else {
      this.oauthService.logOut();
    }
  }
  public refresh(): Promise<void> {
    return this.oauthService.refreshToken().then(() => undefined);
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
}
